import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Toolbar,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Paper,
  Avatar,
  Divider,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff, Business, Security, Speed, TrendingUp } from '@mui/icons-material';
import AppTheme from '../../shared-theme/AppTheme';
import SitemarkIcon from '../../components/SitemarkIcon';
import { useNavigate } from 'react-router-dom';
import useSession from '../../hooks/useSession';

const LoginLayout = (props) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setSession } = useSession();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!username.trim()) newErrors.username = 'Campo requerido';
    if (!password.trim()) newErrors.password = 'Campo requerido';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');

    try {
        const data = await loginUser(username, password);
        console.log('Login exitoso:', data);
        
        // Configurar sesión con el nuevo sistema
        setSession(data.session_token, data);
        
        // Redirigir al dashboard
        navigate('/dashboard');
      } catch (err) {
        setErrorMessage(err.message);
      } finally {
        setIsLoading(false);
      }
  };

  const loginUser = async (username, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          usuario: username,
          password: password
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data?.error || 'Error al iniciar sesión');
      }
      
      return data; // Contendrá el token de sesión y la info del usuario
    } catch (error) {
      console.error('Login error:', error.message);
      throw error;
    }
  };
  

  return (
    <AppTheme {...props}>
        <CssBaseline />
      <Box 
        sx={{ 
          fontFamily: 'Inter, "Noto Sans", sans-serif',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.3
          }}
        />

        {/* Header */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0} 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            px: 4, 
            py: 2 
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'left' }}>
            <Box sx={{ width: 120, maxWidth: '100%', mt:2 }}>
              <img
                src={require('../../logos/BankivoEscalable-Pos.svg').default}
                alt="Bankivo"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '80vh' }}>
            {/* Left Side - Branding */}
            <Box sx={{ flex: 1, pr: 6, display: { xs: 'none', lg: 'block' } }}>
              <Box sx={{ mb: 6 }}>
                <Typography variant="h2" fontWeight={800} color="white" gutterBottom>
                  Bienvenido a
                </Typography>
                 <Box sx={{ width: 300, maxWidth: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mx: 'auto', mt:-3 }}>
                   <img
                     src={require('../../logos/BankivoEscalable-Pos.svg').default}
                     alt="Bankivo"
                     style={{ width: '100%', height: 'auto', display: 'block' }}
                   />
                 </Box>
                <Typography variant="h5" color="rgba(255, 255, 255, 0.8)" sx={{ mb: 4, mt:0 }}>
                  La plataforma financiera más completa para tu negocio
                </Typography>
              </Box>

              {/* Features */}
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                  <Avatar sx={{ bgcolor: 'rgba(85, 180, 172, 0.3)', mr: 2 }}>
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Gestión Empresarial</Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      Administra tu negocio de forma integral
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                  <Avatar sx={{ bgcolor: 'rgba(24, 88, 107, 0.3)', mr: 2 }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Seguridad Avanzada</Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      Tus datos están protegidos con la mejor tecnología
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                  <Avatar sx={{ bgcolor: 'rgba(85, 180, 172, 0.3)', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Análisis Inteligente</Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      Toma decisiones basadas en datos reales
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Right Side - Login Form */}
            <Box sx={{ flex: { xs: 1, lg: 0.6 }, minWidth: 400 }}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CardContent sx={{ p: 6 }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        bgcolor: 'white', 
                        mx: 'auto', 
                        mb: 3,
                        boxShadow: '0 8px 32px rgba(85, 180, 172, 0.3)'
                      }}
                    >
                      <SitemarkIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
                      Iniciar Sesión
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Accede a tu cuenta para continuar
            </Typography>
                  </Box>

                  <Box component="form" noValidate onSubmit={handleSubmit}>
                    {/* Error Message */}
                    {errorMessage && (
                      <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {errorMessage}
                      </Alert>
                    )}

                    <Stack spacing={3}>
                <TextField
                        placeholder="Usuario"
                  fullWidth
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  error={Boolean(errors.username)}
                  helperText={errors.username}
                        InputProps={{ 
                          sx: { 
                            height: 56,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.1)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            }
                          } 
                        }}
                      />

                <TextField
                        placeholder="Contraseña"
                  fullWidth
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  InputProps={{
                          sx: { 
                            height: 56,
                            paddingRight: 1,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.1)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            }
                          },
                    endAdornment: (
                      <InputAdornment position="end">
                              <IconButton 
                                onClick={() => setShowPassword(!showPassword)} 
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                      <Button
                        type="submit"
                        variant="contained"
                        color="primarybutton"
                        fullWidth
                        size="large"
                        disabled={isLoading}
                        sx={{ 
                          height: 56,
                          borderRadius: 2,
                          textTransform: 'none', 
                          fontWeight: 600, 
                          fontSize: '1rem',
                          background: 'linear-gradient(45deg, #55b4ac 0%, #18586b 100%)',
                          boxShadow: '0 8px 32px rgba(85, 180, 172, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #4ba39c 0%, #154a5a 100%)',
                            boxShadow: '0 12px 40px rgba(85, 180, 172, 0.4)',
                          },
                          '&:disabled': {
                            background: 'rgba(85, 180, 172, 0.5)',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        {isLoading ? 'Iniciando sesión...' : 'Ingresar a Bankivo'}
                      </Button>
                    </Stack>

                    <Divider sx={{ my: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        o
                      </Typography>
                    </Divider>

                    <Typography 
                      variant="body2" 
                      color="primary.main" 
                      align="center" 
                      sx={{ 
                        textDecoration: 'none',
                        cursor: 'pointer',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                ¿Olvidaste tu contraseña?
              </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>
    </AppTheme>
  );
};

export default LoginLayout;
